import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Anchor, Grid, Group, Loader, Paper, Stack, Text, Title } from "@mantine/core";
import { useConfirmEmail } from "../api/queries";
import { useLink } from "../router";
import { Header } from "./Header";
import { Shell } from "./Shell";

/**
 * The page an emailed confirmation link lands on.
 *
 * The link is a plain GET the mail client can open, but spending the token is a POST from
 * here: corporate mail scanners follow links, and a GET that consumed the token would burn
 * it before the person ever clicked.
 */
export function ConfirmPage() {
  const { t } = useTranslation();
  const confirm = useConfirmEmail();
  const token = new URLSearchParams(window.location.search).get("token");
  // The token is single-use, so the request must fire exactly once. StrictMode
  // double-invokes effects in development, and without this guard the second call spends a
  // token that is already gone and reports failure on the happy path.
  const fired = useRef(false);
  const home = useLink("/");

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    confirm.mutate(token);
  }, [token, confirm]);

  return (
    <Shell>
      <Shell.Header>
        <Header />
      </Shell.Header>
      <Shell.Main style={{ padding: 20 }}>
        <Grid columns={100}>
          <Grid.Col span={{ base: 100, sm: 80, md: 55 }}>
            <Paper p="md" m={{ base: 0, sm: 20 }} style={{ overflowY: "auto", minHeight: 0 }}>
              <Title order={2} fz="lg" lh={1.5} mb={6}>
                {t("confirmPage.heading")}
              </Title>
              <Stack gap="sm">
                {!token ? (
                  <Alert color="red" variant="light">
                    {t("confirmPage.missingToken")}
                  </Alert>
                ) : confirm.isSuccess ? (
                  <>
                    <Text>{t("confirmPage.success")}</Text>
                    <Anchor {...home}>{t("confirmPage.goToArchive")}</Anchor>
                  </>
                ) : confirm.isError ? (
                  // The server's wording is already written for a person to read, and a
                  // paraphrase here is how the two drift apart.
                  <Alert color="red" variant="light">
                    {(confirm.error as Error).message}
                  </Alert>
                ) : (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="md">{t("confirmPage.confirming")}</Text>
                  </Group>
                )}
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Shell.Main>
    </Shell>
  );
}
